/**
 * Formatters utility module
 * Centralized formatting functions for consistent data presentation
 */

/**
 * Format date to localized string
 * @param {string|Date} iso - ISO date string or Date object
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDate = (iso, options = {}) => {
  try {
    const date = typeof iso === "string" ? new Date(iso) : iso;
    if (isNaN(date.getTime())) return "";

    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      ...options,
    });
  } catch {
    return "";
  }
};

/**
 * Format currency amount with locale-specific formatting
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: 'UAH')
 * @param {Object} options - Intl.NumberFormat options
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = "UAH", options = {}) => {
  try {
    const numAmount = Number(amount);
    if (isNaN(numAmount)) return "0₴";

    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      ...options,
    }).format(numAmount);
  } catch {
    return `${Number(amount) || 0}₴`;
  }
};

/**
 * Format balance amount with Ukrainian hryvnia symbol
 * @param {number} balance - Balance amount
 * @param {Object} options - Formatting options
 * @returns {string} Formatted balance string
 */
export const formatBalance = (balance, options = {}) => {
  let numBalance = Number(balance);
  if (isNaN(numBalance)) numBalance = 0;

  return `${numBalance.toLocaleString(undefined, {
    maximumFractionDigits: 2,
    ...options,
  })}₴`;
};

/**
 * Format number with locale-specific separators
 * @param {number} num - Number to format
 * @param {Object} options - Intl.NumberFormat options
 * @returns {string} Formatted number string
 */
export const formatNumber = (num, options = {}) => {
  try {
    const number = Number(num);
    if (isNaN(number)) return "0";

    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      ...options,
    }).format(number);
  } catch {
    return String(num || 0);
  }
};

/**
 * Format percentage with proper decimal places
 * @param {number} value - Percentage value (0-100)
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = 0) => {
  const numValue = Number(value);
  if (isNaN(numValue)) return "0%";

  return `${numValue.toFixed(decimals)}%`;
};

/**
 * Format file size in human-readable format
 * @param {number} bytes - File size in bytes
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted file size string
 */
export const formatFileSize = (bytes, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

/**
 * Format relative time (e.g., "2 hours ago")
 * @param {string|Date} date - Date to format
 * @param {Object} options - Intl.RelativeTimeFormat options
 * @returns {string} Formatted relative time string
 */
export const formatRelativeTime = (date, options = {}) => {
  try {
    const now = new Date();
    const targetDate = typeof date === "string" ? new Date(date) : date;

    if (isNaN(targetDate.getTime())) return "";

    const diffInSeconds = Math.floor((now - targetDate) / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    const rtf = new Intl.RelativeTimeFormat(undefined, {
      numeric: "auto",
      ...options,
    });

    if (Math.abs(diffInDays) >= 1) {
      return rtf.format(-diffInDays, "day");
    } else if (Math.abs(diffInHours) >= 1) {
      return rtf.format(-diffInHours, "hour");
    } else if (Math.abs(diffInMinutes) >= 1) {
      return rtf.format(-diffInMinutes, "minute");
    } else {
      return rtf.format(-diffInSeconds, "second");
    }
  } catch {
    return "";
  }
};

const formatters = {
  formatDate,
  formatCurrency,
  formatBalance,
  formatNumber,
  formatPercentage,
  formatFileSize,
  formatRelativeTime,
};

export default formatters;
